function runtimeEnv(key: string): string | undefined {
  const runtimeValue = window.__ENV__?.[key];
  if (runtimeValue !== undefined && runtimeValue !== "") {
    return runtimeValue;
  }

  return process.env[key];
}

export const firebaseAPIKey = runtimeEnv("REACT_APP_FIREBASE_API_KEY");
export const resourceAPIURL = runtimeEnv("REACT_APP_RESOURCE_API_URL");

function parseTimeoutMs(
  value: string | undefined,
  fallbackMs: number
): number {
  const parsed = parseInt(value || String(fallbackMs), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackMs;
}

/** Default timeout for JSON API requests (ms). Override via REACT_APP_API_REQUEST_TIMEOUT_MS. */
export const apiRequestTimeoutMs = parseTimeoutMs(
  runtimeEnv("REACT_APP_API_REQUEST_TIMEOUT_MS"),
  60000
);

/** Longer timeout for file download/upload requests (ms). Override via REACT_APP_API_FILE_REQUEST_TIMEOUT_MS. */
export const apiFileRequestTimeoutMs = parseTimeoutMs(
  runtimeEnv("REACT_APP_API_FILE_REQUEST_TIMEOUT_MS"),
  120000
);

/** Heavy read endpoints such as dashboard / performance stats (ms). Override via REACT_APP_API_LONG_RUNNING_REQUEST_TIMEOUT_MS. */
export const apiLongRunningRequestTimeoutMs = parseTimeoutMs(
  runtimeEnv("REACT_APP_API_LONG_RUNNING_REQUEST_TIMEOUT_MS"),
  180000
);

/**
 * Lead import/export (ms). Set just under Azure's 230s front-end (ARR) idle
 * timeout, which is the hard ceiling for a synchronous request — past that the
 * platform returns a 502 regardless. Override via REACT_APP_API_IMPORT_REQUEST_TIMEOUT_MS.
 */
export const apiImportRequestTimeoutMs = parseTimeoutMs(
  process.env.REACT_APP_API_IMPORT_REQUEST_TIMEOUT_MS,
  220000
);

/**
 * Booking a shipment (ms). The API holds this request open while it waits for the courier
 * to publish the label - Proovia takes about a minute and the API retries for up to three,
 * which the 60s default cuts short. A cut request does not undo anything: the order is
 * placed and the part is shipped, the caller just never sees the label. Sits just under
 * Azure's 230s ARR ceiling, same as the import timeout.
 * Override via REACT_APP_API_SHIPMENT_REQUEST_TIMEOUT_MS.
 */
export const apiShipmentRequestTimeoutMs = parseTimeoutMs(
  process.env.REACT_APP_API_SHIPMENT_REQUEST_TIMEOUT_MS,
  220000
);

export const wilmaSSODashboardUrl = runtimeEnv("REACT_APP_WILMA_SSO_DASHBOARD_URL");
export const wilmaSSOAPIUrl = runtimeEnv("REACT_APP_WILMA_SSO_API_URL");
export const wilmaSSOAppRegistration = runtimeEnv("REACT_APP_WILMA_SSO_APP_REGISTRATION");
export const wilmaSAVCustomerId = runtimeEnv("REACT_APP_WILMA_SAV_CUSTOMER_ID");

export const sentryDSN = runtimeEnv("REACT_APP_SENTRY_DSN");
export const sentryReplaySampleRate = parseFloat(runtimeEnv("REACT_APP_SENTRY_REPLAY_SAMPLE_RATE") || "0.1");
export const sentryReplayOnErrorSampleRate = parseFloat(runtimeEnv("REACT_APP_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE") || "1.0");

// export const wilmaPublicCdn = "https://cdn.recyclingsoftware.co.uk/public/images/";

export const tokenStorageKey = "wilma-app-token";

/** True under `npm start` (react-scripts sets NODE_ENV=development). Gates dev-only helpers. */
export const isLocalDev = process.env.NODE_ENV === "development";

export const rsCustomerIdWhitelist = (runtimeEnv("REACT_APP_RS_WHITELISTED_CUSTOMER_IDS") || "").split(",")
  .map(id => id.trim().toLowerCase())
  .filter(id => id.length > 0);

export const gtWidgetURL = runtimeEnv("REACT_APP_GT_WIDGET_URL") || "http://localhost:8080";
